'use client';

import { amplitudeSendEvent, GTM_NOTSET_STRING, pushDataLayer } from '@musinsa/frontend-utils';
import { Button, LoadingDark, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';

import { createAccountInfoService } from '@musinsa/services-curator';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { getPaths, useCuratorBroadcastPublisher } from '../../common';
import { useCuratorSnackbar } from '../../common/hooks';
import { useNavigate } from '../../common/hooks/useNavigate';
import { transformSettlementDataToServiceRequest } from '../../me/components/utils/transformSettlementData';
import { curatorQueries } from '../../query-factory';
import {
  SettlementForm,
  type SettlementFormRef,
} from '../../settlement/components/SettlementInfoForm/SettlementForm';
import { SettlementFormHeader } from '../../settlement/components/SettlementInfoForm/SettlementFormHeader';
import { getSettlementFormDefaults } from '../../settlement/components/SettlementInfoForm/settlement-form.schema';
import type { SettlementFormData } from '../../settlement/components/SettlementInfoForm/types';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { useAccountInfoForm } from '../hooks/useAccountInfoForm';
import { accountEditQueries } from '../query-factory';

interface ApiLikeError extends Error {
  data?: { meta?: { message?: string } };
  status?: number;
}

const NETWORK_FAILED_UPDATE_MESSAGE =
  '정산 정보 수정에 실패하였습니다. 문제가 계속된다면 PC에서 시도해주세요.';
const NETWORK_FAILED_CREATE_MESSAGE =
  '정산 정보 등록에 실패하였습니다. 문제가 계속된다면 PC에서 시도해주세요.';
const UPDATE_FAILED_MESSAGE = '정산 정보 수정에 실패했습니다. 잠시 후 다시 시도해주세요.';
const CREATE_FAILED_MESSAGE = '정산 정보 등록에 실패했습니다. 잠시 후 다시 시도해주세요.';

interface SettlementEditFormProps {
  /**
   * 진입 모드. create=등록 전용(getAccountInfo 미호출, 미등록/안내), edit=변경(getAccountInfo 호출, 마스킹 수정).
   * 라우트 분리(`/create/account`·`/edit/account`)에 따라 페이지가 지정한다. (SID-4631)
   */
  mode: 'create' | 'edit';
}

export const SettlementEditForm = ({ mode }: SettlementEditFormProps) => {
  const formRef = useRef<SettlementFormRef>(null);
  const { navigateBack, navigateReplace } = useNavigate();
  const { triggerQuerySync } = useCuratorBroadcastPublisher();
  const { isWebview, service } = useDeviceInfo();
  const paths = getPaths(service);
  const snackbar = useCuratorSnackbar();
  const isCreate = mode === 'create';

  // 정산 진입 게이트 — mypage 로 입력 가능 여부(showAccountInfo)·정보 존재 여부를 먼저 판정한다.
  // /me 인라인 정산 UI 가 설정(account-edit)으로 이전되며, 미등록 사용자가 직접 진입 시
  // getAccountInfo 가 실패해 '불러오기 실패' 에러가 노출되던 문제를 3분기(안내/등록/수정)로 해소. (CMQA-7095 / SID-4156)
  // showAccountInfo 는 무신사·29CM 공통 필드라 양 서비스 모두 호출해 게이트로 쓴다. 무신사 경로가
  // mypage 를 안 찔러 게이트가 무력화되고 입력 불가 사용자에게 로딩실패가 뜨던 문제 수정. (SID-4631)
  const { data: mypageData, isPending: isMypagePending } = useQuery({
    ...curatorQueries.mypage.data(),
  });
  // 입력 가능 여부는 서비스 무관 showAccountInfo(수익 발생 여부)로 판정.
  const settlementEligible = mypageData?.showAccountInfo === true;
  // 등록 여부 — mypage accountInfo 의 bank 정보 존재로 판정(무신사·29CM 공용 필드). (SID-4631)
  const isRegistered = !!(
    mypageData?.accountInfo?.bankName && mypageData?.accountInfo?.bankAccountNumber
  );

  // 라우트↔상태 불일치 가드(딥링크): create 인데 이미 등록 → /edit/account, edit 인데 미등록 → /create/account.
  // showAccountInfo/accountInfo 는 middleware auth 에 없어 분기는 클라이언트(mypage)에서만 가능. (SID-4631)
  const routeMismatch = !isMypagePending && (isCreate ? isRegistered : !isRegistered);
  useEffect(() => {
    if (!routeMismatch) return;
    navigateReplace(isCreate ? paths.accountEdit() : paths.accountCreate());
  }, [routeMismatch, isCreate, navigateReplace, paths]);

  // 풀데이터(PII) 조회는 변경(edit)·등록자에 한해. 등록(create)은 미호출(사전수집 금지 정책 정합). (SID-4631)
  const enableAccountFetch = !isCreate && isRegistered;

  const {
    formDefaultValues,
    accountDisplayInfo,
    hasExistingAccountInfo,
    isPending: isAccountPending,
    isError,
  } = useAccountInfoForm({ enabled: enableAccountFetch });

  // 비활성 쿼리는 isPending 이 true 로 남으므로(react-query), 활성일 때만 로딩으로 본다.
  const isLoading = isMypagePending || (enableAccountFetch && isAccountPending);

  const { mutate: updateAccountInfo, isPending: isUpdatePending } = useMutation({
    mutationFn: async (data: SettlementFormData) => {
      const serviceRequest = transformSettlementDataToServiceRequest(data);
      return createAccountInfoService(serviceRequest);
    },
    onSuccess: () => {
      triggerQuerySync({
        queryKeys: [
          accountEditQueries.accountInfo.detail.queryKey,
          curatorQueries.mypage.data().queryKey,
        ],
      });

      snackbar.show({
        message: hasExistingAccountInfo ? '정산 정보를 변경했습니다.' : '정산 정보를 등록했습니다.',
      });

      navigateBack();
    },
    onError: (error: Error) => {
      const apiError = error as ApiLikeError;

      const apiMessage = apiError.data?.meta?.message;
      if (apiMessage) {
        snackbar.error(apiMessage);
        return;
      }

      // HTTP 응답 없음 = 네트워크/업로드 단계 실패 (Android WebView content:// URI 만료 포함).
      // 외부 브라우저로의 복구 경로를 안내한다.
      if (apiError.status === undefined) {
        snackbar.error(
          hasExistingAccountInfo ? NETWORK_FAILED_UPDATE_MESSAGE : NETWORK_FAILED_CREATE_MESSAGE,
        );
        return;
      }

      snackbar.error(hasExistingAccountInfo ? UPDATE_FAILED_MESSAGE : CREATE_FAILED_MESSAGE);
    },
  });

  const handleFormSubmit = (data: SettlementFormData | Partial<SettlementFormData>) => {
    const baseValues = getSettlementFormDefaults(formDefaultValues ?? {});
    const mergedValues = {
      ...baseValues,
      ...data,
    } as SettlementFormData;

    updateAccountInfo(mergedValues);
  };

  const handleUpdateClick = () => {
    if (!isLoading && !isUpdatePending) {
      if (service === SERVICE['29CM']) {
        track({
          schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
          data: {
            page_name: 'curator_settlement_info',
            section_name: 'SettlementInformation',
            section_title: 'SettlementInformation',
            position: -1,
            button_id: 'update',
            button_name: '변경하기',
          },
        });
      }

      if (service === SERVICE.MUSINSA) {
        amplitudeSendEvent({
          action_name: 'click',
          button_name: 'account_edit_complete',
          component_name: 'button',
          event_name: 'click_button',
          from: 'screen_curator_mypage',
          section_name: 'mypage_account',
        });

        pushDataLayer('click_button', {
          section_name: 'mypage_account',
          section_index: '7',
          button_id: 'account_edit_complete',
          index: GTM_NOTSET_STRING,
          brand_id: GTM_NOTSET_STRING,
        });
      }

      formRef.current?.submit();
    }
  };

  const handleCancelClick = () => {
    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
        data: {
          page_name: 'curator_settlement_info',
          section_name: 'SettlementInformation',
          section_title: 'SettlementInformation',
          position: -1,
          button_id: 'cancel',
          button_name: '취소하기',
        },
      });
    }

    navigateBack();
  };

  if (isLoading) {
    return isWebview ? null : (
      <div className="w-full px-4 pt-3 bg-white">
        <SettlementFormHeader title="정산 정보" className="pt-3" />
        <div className="fixed inset-0 flex justify-center items-center">
          <LoadingDark size="size24x24" />
        </div>
      </div>
    );
  }

  // 불일치(딥링크)로 리다이렉트 중 — 잘못된 화면 깜빡임 방지. (SID-4631)
  if (routeMismatch) return null;

  // 입력 불가(showAccountInfo=false, 무신사·29CM 공통 — 수익 미발생) — 정산 PII 사전 수집 금지
  // 정책에 따라 안내 문구만 노출. 등록(create) 진입에서만(변경은 등록 전제). (CMQA-7095 / SID-4631)
  if (isCreate && !settlementEligible) {
    return (
      <div className="w-full px-4 pt-3 bg-white grow flex flex-col">
        <SettlementFormHeader title="정산 정보" className="pt-3" />
        <div className="flex flex-col items-center justify-center grow px-4 py-10">
          <Typography size="14" weight="medium">
            큐레이터 수익 발생 이후 등록 가능합니다.
          </Typography>
        </div>
      </div>
    );
  }

  if (enableAccountFetch && isError) {
    return (
      <div className="w-full px-4 pt-3 bg-white">
        <SettlementFormHeader title="정산 정보" className="pt-3" />
        <div className="flex justify-center items-center py-10">
          <Typography size="14" weight="medium" className="text-red-500">
            정산정보를 불러오는데 실패했습니다.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full pt-3 bg-white grow flex flex-col">
      <SettlementFormHeader title="정산 정보" className="pt-3  px-4" />

      {/* 정보 없음(등록)이면 isEditMode=false 로 전체 입력 필드 노출, 있으면 수정 모드(마스킹 뷰). */}
      <SettlementForm
        ref={formRef}
        onSubmit={handleFormSubmit}
        defaultValues={formDefaultValues}
        isEditMode={hasExistingAccountInfo}
        accountDisplayInfo={accountDisplayInfo}
        hasExistingAccountInfo={hasExistingAccountInfo}
      />

      <div className="sticky bottom-0 left-0 safe-area-bottom bg-neutral-color-white border-t border-gray-200">
        <div className="flex gap-[8px] mt-3 mb-6 px-4">
          <Button
            type="button"
            variant="secondary"
            size={44}
            className="grow"
            onClick={handleCancelClick}
            disabled={isLoading || isUpdatePending}
          >
            <Typography variant="body14pxMed">취소하기</Typography>
          </Button>

          <Button
            type="button"
            size={44}
            className="grow"
            onClick={handleUpdateClick}
            disabled={isLoading || isUpdatePending}
          >
            <Typography variant="body14pxMed">
              {hasExistingAccountInfo ? '변경하기' : '등록하기'}
            </Typography>
          </Button>
        </div>
      </div>
    </div>
  );
};
